import React, { useEffect, useState } from 'react';
import { DividendHistroyData } from '../../../types/dividendhistory.type';
import { formatDetailDate } from '../../../helpers/date';
import Pagination from '../../../Components/Common/Pagination/Pagination';
import { paginate } from '../../../utils/pagination';
import { formatLocaleNumber } from '../../../helpers/number';
import { Currency, DRP } from '../../../utils/constant';

interface DividendTableProps {
  data: DividendHistroyData;
}

const ITEMS_PER_PAGE = 10;

const DividendTable: React.FC<DividendTableProps> = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const history = [...(data?.history ?? [])].sort(
    (a, b) =>
      new Date(b.div_ex_date).getTime() - new Date(a.div_ex_date).getTime(),
  );
  const isAuStock = history[0]?.div_currency === Currency.AU;

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  if (history.length === 0) return null;

  const pagination = paginate(currentPage, ITEMS_PER_PAGE, history.length);
  const pageItems = history.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  if (pageItems.length === 0) return null;
  return (
    <div className="dh-history-section">
      <div className="dh-table-scroll">
        <table className="dh-table">
          <thead>
            <tr>
              <th></th>
              <th className="dh-col-date">Ex-date</th>
              <th className="dh-col-date">Record date</th>
              <th className="dh-col-date">Pay date</th>
              <th className="dh-col-amount">Net amount</th>
              {isAuStock && (
                <>
                  <th className="dh-col-franked">Franked</th>
                  <th className="dh-col-drp">DRP</th>
                </>
              )}
              <th className="dh-col-type">Type</th>
              <th className="dh-col-date">Period end date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item, i) => (
              <tr key={`${item.div_ex_date}-${i}`}>
                <td></td>
                <td className="dh-col-date">
                  {formatDetailDate(item.div_ex_date, false, 'DMY')}
                </td>
                <td className="dh-col-date">
                  {formatDetailDate(item.div_record_date, false, 'DMY')}
                </td>
                <td className="dh-col-date">
                  {formatDetailDate(item.div_pay_date, false, 'DMY')}
                </td>
                <td className="dh-col-amount">
                  {formatLocaleNumber(item.annualised_div_gross_amount, 2)}
                  {item.annualised_div_gross_amount && (
                    <span className="dh-table-unit"> {item.div_currency}</span>
                  )}
                </td>
                {isAuStock && (
                  <>
                    <td className="dh-col-franked">
                      {formatLocaleNumber(item.frank_pct, 2) ?? '-'}
                      {item.frank_pct && (
                        <span className="dh-table-unit"> %</span>
                      )}
                    </td>
                    <td className="dh-col-drp">
                      {DRP[
                        item.div_reinvestment_plan_available as keyof typeof DRP
                      ] ?? '-'}
                    </td>
                  </>
                )}
                <td className="dh-col-type">{item.div_type_marker_desc}</td>
                <td className="dh-col-date">
                  {formatDetailDate(item.period_end_date, false, 'DMY')}
                </td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {history.length > ITEMS_PER_PAGE && (
        <div className="dh-pagination-container">
          <Pagination
            pagination={pagination}
            onPageClick={setCurrentPage}
            haveLastPage
          />
        </div>
      )}
    </div>
  );
};

export default DividendTable;
